/**
 * <sfk-slideshow> — the homepage hero.
 *
 * Progressive enhancement: the markup renders the first slide on its own, and
 * this element only adds the cross-fade, autoplay and pagination behaviour.
 * Autoplay pauses on hover, on focus, while the tab is hidden, and whenever the
 * visitor prefers reduced motion.
 */
class SfkSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-sfk-slide]'));
    if (this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll('[data-sfk-dot]'));
    this.index = 0;
    this.paused = false;
    this.interval = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.show(0);
    this.setAttribute('data-ready', '');

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.show(i);
        this.restart();
      });
    });

    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.addEventListener('keydown', this.onKeydown);
    this.bindSwipe();

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility = () => {
    if (document.hidden) this.stop();
    else if (!this.paused) this.start();
  };

  onKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      this.show(this.index + 1);
      this.restart();
    } else if (event.key === 'ArrowLeft') {
      this.show(this.index - 1);
      this.restart();
    }
  };

  bindSwipe() {
    let startX = null;
    this.addEventListener(
      'touchstart',
      (e) => {
        startX = e.changedTouches[0].clientX;
      },
      { passive: true }
    );
    this.addEventListener(
      'touchend',
      (e) => {
        if (startX === null) return;
        const delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 45) {
          this.show(delta < 0 ? this.index + 1 : this.index - 1);
          this.restart();
        }
        startX = null;
      },
      { passive: true }
    );
  }

  show(next) {
    const count = this.slides.length;
    this.index = ((next % count) + count) % count;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      // Keep hidden slides out of the tab order.
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
  }

  start() {
    if (!this.autoplay || this.reduceMotion.matches || this.timer) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    if (!this.paused) this.start();
  }

  pause = () => {
    this.paused = true;
    this.stop();
  };

  resume = () => {
    this.paused = false;
    this.start();
  };
}

if (!customElements.get('sfk-slideshow')) {
  customElements.define('sfk-slideshow', SfkSlideshow);
}
